import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
} from "react";
import JsSIP from "jssip";
import WebPhonePanel from "../../components/WebPhone/WebPhonePanel";
import { AuthContext } from "../Auth/AuthContext";

// Global, persistent softphone. Owns the jsSIP UA so registration survives
// navigation between screens — leaving the Extensions page only minimizes the
// widget (it never tears the UA down). Mounted once, above the route Switch.
const WebPhoneContext = createContext(null);

export const useWebPhone = () => useContext(WebPhoneContext);

const pcConfig = { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] };

// Persistimos a conexão ativa (ramal + credenciais + estado minimizado) para o
// softphone sobreviver a um reload da página: a UA jsSIP é em memória e morre no
// refresh, então guardamos o necessário e re-registramos ao montar o provider.
const STORE_KEY = "nb_webphone";
const loadStored = () => {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    const v = raw ? JSON.parse(raw) : null;
    return v && v.ext && v.wss && v.realm ? v : null;
  } catch (e) {
    return null;
  }
};

// seleção de dispositivos de áudio (entrada/saída) — persistida à parte do ramal
const DEV_KEY = "nb_webphone_devices";
const loadDevSel = () => {
  try {
    const v = JSON.parse(localStorage.getItem(DEV_KEY));
    return v && typeof v === "object" ? v : {};
  } catch (e) {
    return {};
  }
};

export const WebPhoneProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const userId = user && user.id != null ? String(user.id) : null;
  // só restaura o ramal salvo se for do MESMO usuário logado — não vaza entre contas
  const persisted = loadStored();
  const stored = persisted && String(persisted.userId) === userId ? persisted : null;
  const uaRef = useRef(null);
  const sessionRef = useRef(null);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  // identidade/credenciais do ramal ativo (restaura do localStorage no reload)
  const [conn, setConn] = useState(
    stored ? { ext: stored.ext, realm: stored.realm, wss: stored.wss } : null
  );
  const [visible, setVisible] = useState(!!stored); // widget montado (chip/painel)
  const [minimized, setMinimized] = useState(stored ? stored.minimized !== false : false);

  const [status, setStatus] = useState("idle"); // idle|connecting|registered|failed
  const [call, setCall] = useState("none"); // none|outgoing|incoming|in-call
  const [peer, setPeer] = useState("");
  const [number, setNumber] = useState("");
  const [muted, setMuted] = useState(false);
  const [seconds, setSeconds] = useState(0);

  // dispositivos de áudio: lista + entrada (mic) e saída (alto-falante) escolhidos
  const devSel = loadDevSel();
  const [audioDevices, setAudioDevices] = useState([]);
  const [inputId, setInputId] = useState(devSel.inputId || "");
  const [outputId, setOutputId] = useState(devSel.outputId || "");
  const inputIdRef = useRef(devSel.inputId || "");
  const outputIdRef = useRef(devSel.outputId || "");

  // lista os dispositivos de áudio (pede permissão 1x p/ os rótulos aparecerem)
  const refreshDevices = useCallback(async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return;
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true });
        s.getTracks().forEach(t => t.stop());
      } catch (e) {
        /* sem permissão: a lista vem sem rótulos */
      }
      const list = await navigator.mediaDevices.enumerateDevices();
      setAudioDevices(list.filter(d => d.kind === "audioinput" || d.kind === "audiooutput"));
    } catch (e) {
      /* ignore */
    }
  }, []);

  // persiste a seleção e aplica a SAÍDA (setSinkId) no elemento de áudio
  useEffect(() => {
    inputIdRef.current = inputId;
    outputIdRef.current = outputId;
    try {
      localStorage.setItem(DEV_KEY, JSON.stringify({ inputId, outputId }));
    } catch (e) {
      /* ignore */
    }
    const el = audioRef.current;
    if (el && outputId && typeof el.setSinkId === "function") el.setSinkId(outputId).catch(() => {});
  }, [inputId, outputId]);

  const startTimer = () => {
    setSeconds(0);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
  };
  const stopTimer = () => clearInterval(timerRef.current);

  const clearCall = useCallback(() => {
    stopTimer();
    sessionRef.current = null;
    setCall("none");
    setPeer("");
    setMuted(false);
    if (audioRef.current) audioRef.current.srcObject = null;
  }, []);

  const bindSession = useCallback(
    session => {
      sessionRef.current = session;
      session.on("peerconnection", e => {
        e.peerconnection.addEventListener("track", ev => {
          const el = audioRef.current;
          if (el) {
            el.srcObject = ev.streams[0];
            // roteia pra saída escolhida (alto-falante/fone)
            if (outputIdRef.current && typeof el.setSinkId === "function") el.setSinkId(outputIdRef.current).catch(() => {});
          }
        });
      });
      session.on("confirmed", () => {
        setCall("in-call");
        startTimer();
      });
      session.on("ended", clearCall);
      session.on("failed", clearCall);
    },
    [clearCall]
  );

  // (re)build the UA whenever the active extension/credentials change. Tearing
  // down only happens here (new ext) or on explicit hangUpAndClose — NOT on
  // navigation, so the registration is persistent.
  const ext = conn && conn.ext;
  const realm = conn && conn.realm;
  const wss = conn && conn.wss;
  const extId = ext && ext.id;

  useEffect(() => {
    if (!conn) return undefined;
    if (!wss || !realm || !ext) {
      setStatus("failed");
      return undefined;
    }
    setStatus("connecting");
    let ua;
    try {
      const socket = new JsSIP.WebSocketInterface(wss);
      ua = new JsSIP.UA({
        sockets: [socket],
        uri: `sip:${ext.sipUsername}@${realm}`,
        password: ext.secret,
        display_name: ext.name || ext.sipUsername,
        register: true,
        session_timers: false,
      });
      uaRef.current = ua;
      ua.on("registered", () => setStatus("registered"));
      ua.on("unregistered", () => setStatus("idle"));
      ua.on("registrationFailed", () => setStatus("failed"));
      ua.on("disconnected", () => setStatus("failed"));
      ua.on("newRTCSession", ({ session, originator }) => {
        if (sessionRef.current) {
          session.terminate();
          return;
        }
        bindSession(session);
        if (originator === "remote") {
          setCall("incoming");
          setPeer((session.remote_identity && session.remote_identity.uri.user) || "?");
          setMinimized(false); // chamada recebida traz o painel à frente
        }
      });
      ua.start();
    } catch (e) {
      setStatus("failed");
    }
    return () => {
      try {
        if (sessionRef.current) sessionRef.current.terminate();
      } catch (e) {
        /* ignore */
      }
      if (ua) ua.stop();
      uaRef.current = null;
      clearCall();
      setStatus("idle");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conn, wss, realm, extId]);

  // persistência: grava a conexão ativa (p/ sobreviver a reload) ou limpa ao fechar
  useEffect(() => {
    try {
      if (visible && conn && conn.ext) {
        localStorage.setItem(
          STORE_KEY,
          JSON.stringify({ userId, ext: conn.ext, realm: conn.realm, wss: conn.wss, minimized })
        );
      } else {
        localStorage.removeItem(STORE_KEY);
      }
    } catch (e) {
      /* ignore (modo privado/quota) */
    }
  }, [visible, conn, minimized, userId]);

  // isolamento por usuário: ao trocar de conta (ou deslogar), desliga o webphone do
  // usuário anterior — setConn(null) dispara o teardown do UA (desregistra o ramal) e
  // limpa o widget/credencial. Não roda no mount inicial (ref começa no userId atual).
  const lastUserRef = useRef(userId);
  useEffect(() => {
    if (lastUserRef.current === userId) return;
    lastUserRef.current = userId;
    setConn(null);
    setVisible(false);
    setMinimized(false);
    try {
      localStorage.removeItem(STORE_KEY);
    } catch (e) {
      /* ignore */
    }
  }, [userId]);

  // ---- actions exposed via context ----
  const openWebphone = useCallback((nextExt, nextRealm, nextWss) => {
    setConn(prev => {
      // same extension already connected → just surface the widget
      if (prev && prev.ext && nextExt && prev.ext.id === nextExt.id) return prev;
      return { ext: nextExt, realm: nextRealm, wss: nextWss };
    });
    setNumber("");
    setVisible(true);
    setMinimized(false);
  }, []);

  const minimize = useCallback(() => setMinimized(true), []);
  const expand = useCallback(() => setMinimized(false), []);

  // fecha de vez: desregistra e some o widget
  const closeWebphone = useCallback(() => {
    setConn(null);
    setVisible(false);
    setMinimized(false);
  }, []);

  const dial = useCallback(
    target => {
      const n = (target != null ? target : number).trim();
      if (!n || !uaRef.current || status !== "registered" || sessionRef.current) return;
      setCall("outgoing");
      setPeer(n);
      uaRef.current.call(`sip:${n}@${realm}`, {
        mediaConstraints: { audio: inputIdRef.current ? { deviceId: { exact: inputIdRef.current } } : true, video: false },
        pcConfig,
      });
    },
    [number, status, realm]
  );

  const answer = useCallback(() => {
    if (!sessionRef.current) return;
    sessionRef.current.answer({ mediaConstraints: { audio: inputIdRef.current ? { deviceId: { exact: inputIdRef.current } } : true, video: false }, pcConfig });
  }, []);

  const hangup = useCallback(() => {
    if (sessionRef.current) sessionRef.current.terminate();
    else clearCall();
  }, [clearCall]);

  const toggleMute = useCallback(() => {
    const s = sessionRef.current;
    if (!s) return;
    setMuted(m => {
      if (m) s.unmute({ audio: true });
      else s.mute({ audio: true });
      return !m;
    });
  }, []);

  // teclado: durante a chamada envia DTMF; fora dela monta o número
  const pressKey = useCallback(
    digit => {
      if (sessionRef.current && call === "in-call") {
        try {
          sessionRef.current.sendDTMF(digit);
        } catch (e) {
          /* ignore */
        }
        return;
      }
      setNumber(n => n + digit);
    },
    [call]
  );

  const backspace = useCallback(() => setNumber(n => n.slice(0, -1)), []);

  const value = {
    ext,
    realm,
    status,
    call,
    peer,
    number,
    muted,
    seconds,
    visible,
    minimized,
    setNumber,
    openWebphone,
    closeWebphone,
    minimize,
    expand,
    dial,
    answer,
    hangup,
    toggleMute,
    pressKey,
    backspace,
    audioDevices,
    inputId,
    outputId,
    setInputId,
    setOutputId,
    refreshDevices,
  };

  return (
    <WebPhoneContext.Provider value={value}>
      {children}
      {/* áudio remoto sempre montado enquanto o widget existir */}
      {visible && <audio ref={audioRef} autoPlay />}
      {visible && !minimized && <WebPhonePanel />}
    </WebPhoneContext.Provider>
  );
};

export default WebPhoneContext;
